import { ArrowLeft, ArrowRight, CheckCircle, Eye, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../../contexts/JourneyContext';
import api from '../../services/api';
import './TemplateSelection.css';

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  thumbnail?: string;
}

const TemplateSelection: React.FC = () => {
  const navigate = useNavigate();
  const { currentJourney } = useJourney();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewingTemplate, setPreviewingTemplate] = useState<string | null>(null);
  const [previewHTML, setPreviewHTML] = useState<string>('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (!currentJourney) {
      navigate('/dashboard');
      return;
    }

    fetchTemplates();
  }, [currentJourney, navigate]);

  const fetchTemplates = async () => {
    try {
      const response = await api.getTemplates();
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      // Fallback to default templates
      setTemplates([
        {
          id: 'harvard',
          name: 'Harvard Resume Template',
          description: '✓ ATS-Optimized: Clean structure passes 95%+ of ATS filters\n✓ Classic format trusted by Harvard Career Services\n✓ Proven track record across ALL industries (Finance, Consulting, Tech, Healthcare)\n✓ Emphasizes achievements and quantifiable impact\n\nBest for: Traditional industries, career changers, and maximum ATS compatibility',
          category: 'Traditional'
        },
        {
          id: 'google',
          name: 'Google Recruiter-Preferred',
          description: '✓ ATS-Optimized: Perfect parsing for tech company systems\n✓ Based on Google\'s internal recruiting guidelines\n✓ Optimized for FAANG, startups, and scale-ups\n✓ Emphasizes metrics, impact, and technical depth\n\nBest for: Software Engineers, Product Managers, Data Scientists, and tech roles',
          category: 'Tech-Focused'
        },
        {
          id: 'impact',
          name: 'Impact-Focused Template',
          description: '✓ ATS-Optimized: Structured for executive-level systems\n✓ Highlights measurable results and ROI at the top\n✓ Perfect for candidates with strong track records\n✓ Emphasizes leadership and business impact\n\nBest for: Senior roles, executives, consultants, and high-impact contributors',
          category: 'Senior/Executive'
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplate(templateId);
  };

  const handlePreviewTemplate = async (templateId: string) => {
    if (!currentJourney) return;
    
    setLoadingPreview(true);
    setPreviewingTemplate(templateId);
    
    try {
      const html = await api.previewTemplate(currentJourney.id, templateId);
      setPreviewHTML(html);
    } catch (error) {
      console.error('Failed to load preview:', error);
      alert('Failed to load preview. Please try again.');
      setPreviewingTemplate(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const closePreview = () => {
    setPreviewingTemplate(null);
    setPreviewHTML('');
  };

  const handleGenerateResume = async () => {
    if (!selectedTemplate || !currentJourney) {
      return;
    }

    setIsGenerating(true);
    try {
      const response = await api.generateResume(
        currentJourney.id,
        selectedTemplate,
        {
          sectionOrder: ['personal', 'summary', 'experience', 'projects', 'education', 'skills'],
          hiddenSections: [],
          colors: {},
          fonts: {},
          spacing: {}
        }
      );

      const resume = response.resume;
      console.log('Resume generated successfully:', resume);
      console.log('Navigating to:', `/resume/${resume.id}`);
      
      // Use window.location to force a full page reload and ensure route exists
      window.location.href = `/resume/${resume.id}`;
    } catch (error: any) {
      console.error('Failed to generate resume:', error);
      alert(error.response?.data?.error || 'Failed to generate resume. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (isLoading) {
    return <div className="loading">Loading templates...</div>;
  }

  return (
    <div className="template-selection-container">
      <div className="template-header">
        <h1>Choose Your ATS-Optimized Template</h1>
        <p>All templates pass ATS filters—and you're already prepared for interviews. Pick one that matches your style.</p>
      </div>

      <div className="templates-grid">
        {templates.map((template) => (
          <div
            key={template.id}
            className={`template-card ${selectedTemplate === template.id ? 'selected' : ''}`}
            onClick={() => handleSelectTemplate(template.id)}
          >
            <div className="template-thumbnail">
              {template.thumbnail ? (
                <img src={template.thumbnail} alt={template.name} />
              ) : (
                <div className="placeholder-thumbnail">
                  <span className="template-category">{template.category}</span>
                  {/* Mock resume preview - different for each template */}
                  {template.id === 'harvard' && (
                    <div style={{fontSize: '0.65rem', lineHeight: '1.4', color: '#1a1a1a', width: '100%', fontFamily: 'Georgia, serif'}}>
                      <div style={{textAlign: 'center', borderBottom: '2px solid #000', paddingBottom: '0.75rem', marginBottom: '1rem'}}>
                        <div style={{fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.25rem'}}>JOHN DOE</div>
                        <div style={{fontSize: '0.55rem'}}>john.doe@email.com • (123) 456-7890 • linkedin.com/in/johndoe</div>
                      </div>
                      <div style={{fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'uppercase', borderBottom: '2px solid #000', paddingBottom: '0.2rem', marginBottom: '0.5rem'}}>EXPERIENCE</div>
                      <div style={{marginBottom: '0.75rem'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', fontWeight: '600'}}>
                          <span>Senior Software Engineer</span>
                          <span style={{fontSize: '0.55rem'}}>2020-Present</span>
                        </div>
                        <div style={{fontSize: '0.58rem', fontStyle: 'italic'}}>Tech Company, San Francisco</div>
                        <div style={{fontSize: '0.6rem', marginTop: '0.25rem'}}>
                          • Increased system efficiency by 45% through optimization<br/>
                          • Led team of 8 engineers in product development
                        </div>
                      </div>
                      <div style={{fontWeight: 'bold', fontSize: '0.75rem', textTransform: 'uppercase', borderBottom: '2px solid #000', paddingBottom: '0.2rem', marginTop: '1rem'}}>EDUCATION</div>
                    </div>
                  )}
                  {template.id === 'google' && (
                    <div style={{fontSize: '0.65rem', lineHeight: '1.35', color: '#202124', width: '100%', fontFamily: 'Arial, sans-serif'}}>
                      <div style={{marginBottom: '1rem'}}>
                        <div style={{fontWeight: 'bold', fontSize: '1.2rem', color: '#1a73e8'}}>John Doe</div>
                        <div style={{fontSize: '0.58rem', color: '#5f6368'}}>Senior Software Engineer | San Francisco, CA</div>
                        <div style={{fontSize: '0.55rem', color: '#5f6368'}}>john.doe@email.com • linkedin.com/in/johndoe • github.com/johndoe</div>
                      </div>
                      <div style={{fontWeight: '600', fontSize: '0.7rem', color: '#1a73e8', marginBottom: '0.4rem'}}>EXPERIENCE</div>
                      <div style={{marginBottom: '0.75rem'}}>
                        <div style={{fontWeight: '600'}}>Software Engineer III • Google</div>
                        <div style={{fontSize: '0.55rem', color: '#5f6368', marginBottom: '0.25rem'}}>Jan 2020 - Present | Mountain View, CA</div>
                        <div style={{fontSize: '0.6rem'}}>
                          • <strong>Reduced latency by 60%</strong> by optimizing query performance<br/>
                          • <strong>Drove $2M revenue increase</strong> through feature launch<br/>
                          • Built ML pipeline processing <strong>10M+ records/day</strong>
                        </div>
                      </div>
                      <div style={{fontWeight: '600', fontSize: '0.7rem', color: '#1a73e8', marginTop: '1rem'}}>TECHNICAL SKILLS</div>
                      <div style={{fontSize: '0.6rem'}}>Python • Go • Kubernetes • TensorFlow • PostgreSQL</div>
                    </div>
                  )}
                  {template.id === 'impact' && (
                    <div style={{display: 'flex', gap: '1rem', fontSize: '0.6rem', lineHeight: '1.3', color: '#2d3748', width: '100%'}}>
                      <div style={{flex: '0 0 35%', borderRight: '3px solid #4299e1', paddingRight: '0.75rem'}}>
                        <div style={{fontWeight: 'bold', fontSize: '0.95rem', marginBottom: '0.5rem'}}>JOHN DOE</div>
                        <div style={{fontSize: '0.55rem', marginBottom: '1rem'}}>
                          Senior Engineer<br/>
                          San Francisco, CA<br/>
                          john@email.com<br/>
                          (123) 456-7890
                        </div>
                        <div style={{fontWeight: 'bold', fontSize: '0.65rem', color: '#2b6cb0', marginBottom: '0.3rem'}}>SKILLS</div>
                        <div style={{fontSize: '0.55rem', marginBottom: '0.75rem'}}>
                          • Python, Java, Go<br/>
                          • AWS, Kubernetes<br/>
                          • PostgreSQL, Redis<br/>
                          • Team Leadership
                        </div>
                        <div style={{fontWeight: 'bold', fontSize: '0.65rem', color: '#2b6cb0'}}>EDUCATION</div>
                        <div style={{fontSize: '0.55rem'}}>
                          <strong>BS Computer Sci</strong><br/>
                          Stanford University<br/>
                          2015-2019
                        </div>
                      </div>
                      <div style={{flex: '1'}}>
                        <div style={{fontWeight: 'bold', fontSize: '0.7rem', color: '#2b6cb0', borderBottom: '2px solid #4299e1', paddingBottom: '0.2rem', marginBottom: '0.5rem'}}>
                          KEY ACHIEVEMENTS
                        </div>
                        <div style={{fontSize: '0.58rem', marginBottom: '0.5rem', padding: '0.4rem', background: '#ebf8ff', borderLeft: '3px solid #4299e1'}}>
                          <strong>$5M revenue impact</strong> through platform optimization
                        </div>
                        <div style={{fontSize: '0.58rem', marginBottom: '0.75rem', padding: '0.4rem', background: '#ebf8ff', borderLeft: '3px solid #4299e1'}}>
                          <strong>85% efficiency gain</strong> in data processing pipeline
                        </div>
                        <div style={{fontWeight: 'bold', fontSize: '0.7rem', color: '#2b6cb0', marginBottom: '0.4rem'}}>EXPERIENCE</div>
                        <div style={{marginBottom: '0.5rem'}}>
                          <div style={{fontWeight: '600'}}>Sr. Engineer • Tech Corp</div>
                          <div style={{fontSize: '0.52rem', color: '#718096'}}>2020-Present</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="template-info">
              <h3>{template.name}</h3>
              <p>{template.description}</p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreviewTemplate(template.id);
                }}
                className="btn-preview"
              >
                <Eye size={16} />
                Preview
              </button>
            </div>
            {selectedTemplate === template.id && (
              <div className="selected-indicator">
                <CheckCircle size={16} />
                <span>Selected</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Preview Modal */}
      {previewingTemplate && (
        <div className="preview-modal" onClick={closePreview}>
          <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h2>Resume Preview: {templates.find(t => t.id === previewingTemplate)?.name}</h2>
              <button onClick={closePreview} className="btn-close">
                <X size={20} />
              </button>
            </div>
            <div className="preview-modal-body">
              {loadingPreview ? (
                <div className="preview-loading">Loading preview...</div>
              ) : (
                <iframe
                  srcDoc={previewHTML}
                  title="Resume Preview"
                  className="preview-iframe"
                  sandbox="allow-same-origin"
                />
              )}
            </div>
            <div className="preview-modal-footer">
              <button onClick={closePreview} className="btn btn-secondary">
                Close
              </button>
              <button
                onClick={() => {
                  setSelectedTemplate(previewingTemplate);
                  closePreview();
                }}
                className="btn btn-primary"
              >
                <CheckCircle size={16} />
                Select Template
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="template-actions">
        <button
          onClick={() => navigate('/journey/review')}
          className="btn btn-secondary"
        >
          <ArrowLeft size={16} />
          Back to Review
        </button>
        <button
          onClick={handleGenerateResume}
          disabled={!selectedTemplate || isGenerating}
          className="btn btn-primary"
        >
          {isGenerating ? 'Generating...' : (
            <>
              Generate Resume
              <ArrowRight size={16} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default TemplateSelection;

